import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Image } from '@swyftx/aviary/atoms/Image';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import {
  ErrorType,
  BannerConfig,
} from '@swyftx/features/loans/components/EducationPage/ErrorBanner/types/errorBannerTypes';

import { ContentBanner } from '@global-components/ContentBanner/ContentBanner';
import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { useEntitySwitch } from '@hooks/Entity/useEntitySwitch';
import { useGlobalResolveRestrictions } from '@hooks/useGlobalResolveRestrictions';

import { observer } from 'mobx-react-lite';
import { useIntercom } from 'react-use-intercom';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

type ErrorBannerProps = {
  errorType: ErrorType | undefined;
};

export const ErrorBanner: React.FC<ErrorBannerProps> = observer(({ errorType }) => {
  const { t } = useTranslation('loans');
  const { navigate } = useNavigateRoute();
  const { openModal } = useModal();
  const resolveRestrictions = useGlobalResolveRestrictions();
  const { selectAccount, entityAccounts } = useEntitySwitch();
  const { addSnackBar } = useSnackBars();
  const { show: showIntercom } = useIntercom();

  // In order of priority
  const bannerConfigMap: Record<ErrorType, BannerConfig> = {
    [ErrorType.nonAuUser]: {
      theme: 'warning',
      title: t('ErrorBanner.nonAuUser.title'),
      description: t('ErrorBanner.nonAuUser.description'),
    },
    [ErrorType.entityType]: {
      theme: 'warning',
      title: t('ErrorBanner.isEntity.title'),
      description: t('ErrorBanner.isEntity.description'),
      actionLabel: t('ErrorBanner.isEntity.actionLabel'),
      onAction: () => {
        const personalAccount = entityAccounts.find((acc) => acc.uuid === 'USER');
        if (personalAccount) {
          selectAccount(personalAccount);
          addSnackBar({
            variant: 'info',
            content: t('ErrorBanner.isEntity.snackBarLabel'),
            showIcon: true,
            duration: 5_000,
          });
        }
      },
    },
    [ErrorType.invalidAddress]: {
      theme: 'warning',
      icon: <Image image='account_restricted' usePalette={false} className='h-64 w-64' alt='account restricted icon' />,
      title: t('ErrorBanner.invalidAddress.title'),
      description: t('ErrorBanner.invalidAddress.description'),
      actionLabel: t('ErrorBanner.invalidAddress.actionLabel'),
      onAction: () => showIntercom(),
    },
    [ErrorType.kyc1]: {
      theme: 'warning',
      title: t('ErrorBanner.Kyc1.title'),
      description: t('ErrorBanner.Kyc1.description'),
      actionLabel: t('ErrorBanner.Kyc1.actionLabel'),
      onAction: () => navigate(NavigationURLs.ProfileVerification),
    },
    [ErrorType.reverificationReq]: {
      theme: 'warning',
      title: t('ErrorBanner.ReverificationReq.title'),
      description: (
        <Body size='small' color='secondary'>
          <Trans i18nKey='ErrorBanner.ReverificationReq.description' t={t} components={[<strong key='strong' />]} />
        </Body>
      ),
      actionLabel: t('ErrorBanner.ReverificationReq.actionLabel'),
      onAction: () => navigate(NavigationURLs.Reverification),
    },
    [ErrorType.reverificationLapsed]: {
      theme: 'error',
      title: t('ErrorBanner.ReverificationLapsed.title'),
      description: t('ErrorBanner.ReverificationLapsed.description'),
      actionLabel: t('ErrorBanner.ReverificationLapsed.actionLabel'),
      onAction: () => navigate(NavigationURLs.Reverification),
    },
    [ErrorType.kyc2Req]: {
      theme: 'warning',
      icon: <Image image='source_of_wealth_check' className='h-64 w-64' usePalette />,
      title: t('ErrorBanner.Kyc2Req.title'),
      description: t('ErrorBanner.Kyc2Req.description'),
      actionLabel: t('ErrorBanner.Kyc2Req.actionLabel'),
      onAction: () => navigate(NavigationURLs.SourceOfWealth),
    },
    [ErrorType.kyc2Lapsed]: {
      theme: 'error',
      title: t('ErrorBanner.Kyc2Lapsed.title'),
      description: t('ErrorBanner.Kyc2Lapsed.description'),
    },
    [ErrorType.userRestricted]: {
      theme: 'warning',
      title: t('ErrorBanner.UserRestricted.title'),
      description: t('ErrorBanner.UserRestricted.description'),
      actionLabel: t('ErrorBanner.UserRestricted.actionLabel'),
      onAction: resolveRestrictions,
      subActionLabel: t('ErrorBanner.UserRestricted.subActionLabel'),
      onSubAction: () => openModal(Modals.RestrictedModal),
    },
  };

  if (!errorType || !bannerConfigMap[errorType]) {
    return <></>;
  }
  const bannerProps = bannerConfigMap[errorType];
  return (
    <ContentBanner icon={<Image image='verification_banner' className='h-64 w-64' usePalette />} {...bannerProps} />
  );
});
ErrorBanner.displayName = 'ErrorBanner';
